// WP-0.7: typed api client. Same-origin fetch (SE-3 CORS lock via the CloudPanel proxy),
// cookies included so the httpOnly session travels (AU-B). Errors surface the shared
// error envelope's code so pages can branch (e.g. LOCKED, RATE_LIMITED).
import { errorEnvelopeSchema } from '@pfos/shared';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: body === undefined ? {} : { 'content-type': 'application/json' },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!res.ok) {
    let code = 'HTTP_ERROR';
    let message = `Request failed (${String(res.status)})`;
    try {
      const parsed = errorEnvelopeSchema.safeParse(await res.json());
      if (parsed.success) {
        code = parsed.data.error.code;
        message = parsed.data.error.message;
      }
    } catch {
      /* non-JSON body — keep the defaults */
    }
    throw new ApiError(res.status, code, message);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string): Promise<T> => request<T>('GET', path),
  post: <T>(path: string, body?: unknown): Promise<T> => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown): Promise<T> => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown): Promise<T> => request<T>('PATCH', path, body),
  // WP-C2.3: DELETE .../events/:uid carries a `{ confirmTitle }` body (SB-1 double-confirm) —
  // an optional second param keeps every other existing bodyless DELETE call site unaffected.
  delete: <T>(path: string, body?: unknown): Promise<T> => request<T>('DELETE', path, body),
};
